/**
 * AuthConfirm Page Component
 * Handles post-registration email confirmation tokens and password reset landing links from Supabase Auth.
 * Parses URL query parameters, updates session state, and redirects authenticated users.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import '../assets/fontello-change/fontello-79be5077/css/fontello.css';
import logo from '../assets/finalclipstonedesginlogo-removebg-preview.png';

const AuthConfirm = () => {
  const navigate = useNavigate();
  // Verification status state ('verifying', 'success', 'error')
  const [status, setStatus]   = useState('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleConfirmation = async () => {
      try {
        const hashParams  = new URLSearchParams(window.location.hash.replace('#', '?'));
        const queryParams = new URLSearchParams(window.location.search);

        // ── Check for any error in the URL first ─────────────────────────────
        const errorParam = hashParams.get('error') || queryParams.get('error');
        const errorDesc  = hashParams.get('error_description') || queryParams.get('error_description');

        if (errorParam) {
          setStatus('error');
          setMessage(
            errorDesc
              ? decodeURIComponent(errorDesc.replace(/\+/g, ' '))
              : 'Verification failed. Please try again.'
          );
          return;
        }

        // ── PKCE flow — Supabase v2 sends ?code= in the URL ──────────────────
        // The Supabase client auto-exchanges it when detectSessionInUrl is true (default).
        // Calling getSession() after a short delay gives it time to finish.
        const code = queryParams.get('code');
        if (code) {
          // Let the Supabase client process the code exchange
          await new Promise((res) => setTimeout(res, 500));
          const { data, error } = await supabase.auth.getSession();

          if (error || !data?.session) {
            setStatus('error');
            setMessage('Could not verify your email. The link may have expired. Please sign up again.');
            return;
          }

          setStatus('success');
          setMessage('Your email has been confirmed! Redirecting you to login...');
          await supabase.auth.signOut();
          setTimeout(() => navigate('/login', { state: { confirmed: true } }), 2500);
          return;
        }

        // ── Legacy hash token flow — older Supabase versions ─────────────────
        const accessToken  = hashParams.get('access_token')  || queryParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');

        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token:  accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            setStatus('error');
            setMessage('Could not verify your email. The link may have expired.');
            return;
          }

          setStatus('success');
          setMessage('Your email has been confirmed! Redirecting you to login...');
          await supabase.auth.signOut();
          setTimeout(() => navigate('/login', { state: { confirmed: true } }), 2500);
          return;
        }

        // ── No tokens at all — check if already has a live session ───────────
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          setStatus('success');
          setMessage('Your email has been confirmed! Redirecting you to login...');
          await supabase.auth.signOut();
          setTimeout(() => navigate('/login', { state: { confirmed: true } }), 2500);
          return;
        }

        // ── Nothing found ─────────────────────────────────────────────────────
        setStatus('error');
        setMessage('No confirmation token found. Please use the link from your email.');

      } catch (err) {
        console.error('Auth confirm error:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    handleConfirmation();
  }, [navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logoImage} />
        </div>
        <h2 id="auth-confirm-title" style={styles.title}>Clipstone Miners Welfare</h2>

        {status === 'verifying' && (
          <div style={styles.verifyingBox}>
            <div style={styles.spinner} />
            <p style={styles.verifyingText}>Verifying your email address...</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.successBox}>
            <div style={styles.checkIcon}><i className="icon-calendar" /></div>
            <h3 style={styles.successTitle}>Email Confirmed!</h3>
            <p style={styles.successText}>{message}</p>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.errorBox}>
            <div style={styles.errorIcon}><i className="icon-left" /></div>
            <h3 style={styles.errorTitle}>Verification Failed</h3>
            <p style={styles.errorText}>{message}</p>
            <button style={styles.button} onClick={() => navigate('/login')}>
              Back to Login
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '40px 20px',
    background: 'linear-gradient(135deg, #f9f8f6 0%, #e8e6e1 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#ffffff',
    padding: '50px',
    borderRadius: '16px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '480px',
    border: '1px solid #e5e5e5',
    textAlign: 'center',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logoImage: {
    height: '100px',
    width: 'auto',
  },
  title: {
    margin: '0 0 32px 0',
    color: '#1e3f32',
    fontSize: '22px',
    fontWeight: '700',
  },
  verifyingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '24px 0',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #1e3f32',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  verifyingText: {
    color: '#6b7280',
    fontSize: '16px',
    margin: 0,
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 0',
  },
  checkIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#ecfdf5',
    border: '2px solid #6ee7b7',
    color: '#059669',
    fontSize: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successTitle: {
    color: '#059669',
    fontSize: '22px',
    fontWeight: '700',
    margin: '8px 0 0 0',
  },
  successText: {
    color: '#6b7280',
    fontSize: '15px',
    margin: 0,
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 0',
  },
  errorIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#fff0f0',
    border: '2px solid #fca5a5',
    color: '#dc2626',
    fontSize: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTitle: {
    color: '#dc2626',
    fontSize: '22px',
    fontWeight: '700',
    margin: '8px 0 0 0',
  },
  errorText: {
    color: '#6b7280',
    fontSize: '15px',
    margin: 0,
  },
  button: {
    marginTop: '12px',
    backgroundColor: '#1e3f32',
    color: '#ffffff',
    padding: '14px 32px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

const styleTag = document.createElement('style');
styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);

export default AuthConfirm;